import { useMutation } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';
import useReportsAmplitude from '@/views/AdminDashboard/hooks/useReportsAmplitude';

import SEND_LEADER_REPORT from './graphql/SendLeaderReport.v4.b2b.graphql';
import type {
  SendLeaderReport,
  SendLeaderReportVariables,
} from './graphql/types/SendLeaderReport';

enum STATUS {
  SUCCESS = 'success',
  FAILED = 'error',
}

export const useLeaderReport = (slug: string) => {
  const { t } = useTranslation();
  const { openSnackbar } = useAdminDashboard();
  const { logSubmit } = useReportsAmplitude(slug);

  const [sendLeaderReport, { data, loading, error }] = useMutation<
    SendLeaderReport,
    SendLeaderReportVariables
  >(SEND_LEADER_REPORT, {
    context: { clientName: 'v4.b2b' },
    onCompleted({ leaderReportProgressCall }) {
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (!leaderReportProgressCall?.success)
        openSnackbar({
          type: 'alert',
          msg: t('SNACKBAR_DEFAULT_ERROR'),
        });
    },
    onError() {
      openSnackbar({
        type: 'alert',
        msg: t('SNACKBAR_DEFAULT_ERROR'),
      });
    },
  });

  const submitReport = async ({
    name = t('REPORT_8_TITLE'),
    description = t('REPORT_8_DESCRIPTION'),
    ...restVariables
  }: Omit<SendLeaderReportVariables, 'slug'>) => {
    const variables = {
      slug,
      name,
      description,
      ...restVariables,
    };
    const { data } = await sendLeaderReport({
      variables,
    });
    logSubmit({
      event: 'Send Report',
      reportType: 'Leader Report',
      filters: variables,
      status: data?.leaderReportProgressCall?.success
        ? STATUS.SUCCESS
        : STATUS.FAILED,
    });
    return data;
  };

  return {
    submitReport,
    loading,
    error,
    data,
  };
};
